/**
 * Declarative field-spec for the master-catalog material forms (filament
 * first; resin / supply follow the same shape). Single source consumed by
 * BOTH platform-admin and tenant so layout, examples, help text, the
 * basic/advanced split and the validation hints are defined ONCE.
 *
 * Rendering contract (implemented by the shared form shell, not here):
 *  - Fields are grouped by `section`; sections render in `MATERIAL_SECTIONS`
 *    order. A section with no visible field is hidden.
 *  - `tier: 'basic'` fields always show; `'advanced'` fields appear only when
 *    the "Avanzado" toggle is on.
 *  - `labelKey` / `helpKey` are i18n keys — tenant resolves them via its
 *    `t()`; admin via a static Spanish fallback map (admin stays out of the
 *    i18n runtime). `placeholder` is a LITERAL example shown as the field's
 *    background hint (disappears on typing).
 *  - `helpKey` is the simplified, one-line explanation surfaced by the "?"
 *    popover in each section header (the `<SectionHelp>` component).
 *  - Numeric `min`/`max` mirror the DB column precision pinned in
 *    `bolloprint_shared/master_material.py` (filament_diameter → Numeric(3,2)
 *    max 9.99; density → Numeric(4,3) max 9.999). Keep these in sync with the
 *    backend bounds when they change.
 *
 * `labelKey`s reuse the EXISTING tenant locale keys wherever they already
 * exist (so no translation is duplicated); only the help keys, sectionAppearance,
 * and a few fields (gtin/sku/shore A+D/print-temp min+max) are new.
 */
import { FILAMENT_POLYMER_LIST, RESIN_POLYMER_LIST } from './materialPolymers';
import type { IconName } from '../components/Icon';

export type FieldTier = 'basic' | 'advanced';

export type MaterialSectionKey =
  | 'identity'
  | 'appearance'
  | 'material'
  | 'printing'
  | 'drying'
  | 'viscosity'
  | 'curing'
  | 'links'
  | 'notes';

export type FieldControl =
  | 'text'
  | 'number'
  | 'select'
  | 'colors'
  | 'tags'
  | 'certifications'
  | 'image'
  | 'textarea'
  | 'slug'
  | 'url';

export interface MaterialSectionSpec {
  key: MaterialSectionKey;
  /** i18n key for the section heading. */
  labelKey: string;
  /** Nombre del icono en Material Symbols (ver shared/ui/components/Icon.tsx). */
  icon: IconName;
  /** Grid column count for the section body (default 2). */
  columns?: 1 | 2 | 3;
}

export interface MaterialFieldSpec {
  /** Payload key — matches the Pydantic field in master_material.py. */
  key: string;
  section: MaterialSectionKey;
  tier: FieldTier;
  control: FieldControl;
  /** For `select` controls: which option set the renderer feeds in. Explicit
   *  so a new select field can never silently fall through to the wrong list.
   *  'manufacturers'/'materialTypes' use the dedicated props; anything else is
   *  looked up in the form's `optionSets` (e.g. 'kinds', 'countries'). */
  optionsKey?: 'manufacturers' | 'materialTypes' | 'kinds' | 'countries';
  /** i18n key for the field label. */
  labelKey: string;
  /** i18n key for the simplified help shown in the section "?" popover. */
  helpKey: string;
  /** Literal example shown as the input's background placeholder. */
  placeholder?: string;
  /** Unit suffix shown next to the control (mm, g/cm³, °C, g, min). The label
   *  i18n string must NOT repeat the unit — the renderer appends it once. */
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  maxLength?: number;
  /** Grid column span within the section (default 1), capped at the section's
   *  column count. Full-width controls (textarea/tags/image/colors/certs)
   *  always span the full row regardless of this. */
  span?: number;
  /** Identity bespoke layout only:
   *  - `'aside'` fields render in a full-width 3-column row BELOW the image +
   *    right-hand grid (e.g. GTIN + the two SKUs).
   *  - `'full'` fields render as their own full-width (single-column) row below
   *    the aside row — for a value too long for the grid (e.g. the product URL).
   */
  slot?: 'aside' | 'full';
  /** Required field: the form shows the red asterisk next to its label
   *  (Standards §14.7). Only the mark — validation stays with the wrapper. */
  required?: boolean;
}

export const MATERIAL_SECTIONS: readonly MaterialSectionSpec[] = [
  { key: 'identity', labelKey: 'catalog.sectionIdentity', icon: 'badge' },
  { key: 'appearance', labelKey: 'catalog.sectionAppearance', icon: 'palette' },
  { key: 'material', labelKey: 'catalog.sectionMaterial', icon: 'science' },
  { key: 'printing', labelKey: 'catalog.sectionTemperatures', icon: 'thermostat' },
  { key: 'drying', labelKey: 'catalog.sectionDrying', icon: 'dry' },
  { key: 'viscosity', labelKey: 'catalog.sectionViscosity', icon: 'water_drop' },
  { key: 'curing', labelKey: 'catalog.sectionCuring', icon: 'flare' },
  { key: 'links', labelKey: 'catalog.sectionLinks', icon: 'link' },
  { key: 'notes', labelKey: 'catalog.sectionExtras', icon: 'notes' },
] as const;

/**
 * Filament section layout: `identity` uses the bespoke image + aside layout
 * (triggered by the image field living in it), and `material` + `printing`
 * render in 3 columns. Pass this as the `sections` prop from the filament
 * wrappers; the other entities keep the default 2-column `MATERIAL_SECTIONS`.
 */
export const FILAMENT_SECTIONS: readonly MaterialSectionSpec[] = MATERIAL_SECTIONS.map((s) =>
  s.key === 'material' || s.key === 'printing' ? { ...s, columns: 3 } : s,
);

/**
 * Resin section layout: like the default 2-column `MATERIAL_SECTIONS`, but the
 * `material` section renders in 3 columns so Volumen · Densidad · Peso share a
 * row and Dureza A · Dureza D share the next. Pass this as the `sections` prop
 * from the resin wrappers (tenant + admin).
 */
export const RESIN_SECTIONS: readonly MaterialSectionSpec[] = MATERIAL_SECTIONS.map((s) =>
  s.key === 'material' ? { ...s, columns: 3 } : s,
);

/**
 * Filament (FFF) field-spec. `material_type` is rendered as a closed select
 * (FFF whitelist); `primary_color_hex` uses the compound colors control which
 * also writes `secondary_colors_hex`. `is_active` is NOT here — the shell
 * handles it as a status switch in edit mode.
 */
export const FILAMENT_FIELDS: readonly MaterialFieldSpec[] = [
  // ── Identidad (bespoke layout: tall image on the left, 2-col grid on the
  //    right; the `aside` fields — GTIN + SKUs — form a 3-col row below.
  //    See MaterialForm's identity branch) ───────────────────────────────────
  { key: 'image_url', section: 'identity', tier: 'basic', control: 'image',
    labelKey: 'catalog.fieldImage', helpKey: 'catalog.helpImage' },
  { key: 'manufacturer_id', section: 'identity', tier: 'basic', control: 'select', optionsKey: 'manufacturers', span: 2,
    required: true, labelKey: 'catalog.fieldManufacturer', helpKey: 'catalog.helpManufacturer' },
  { key: 'material_name', section: 'identity', tier: 'basic', control: 'text', maxLength: 80, span: 2,
    required: true, placeholder: 'PLA Galaxy Black', labelKey: 'catalog.fieldMaterialName', helpKey: 'catalog.helpMaterialName' },
  { key: 'material_type', section: 'identity', tier: 'basic', control: 'select', optionsKey: 'materialTypes',
    labelKey: 'catalog.fieldMaterialType', helpKey: 'catalog.helpMaterialType' },
  // Peso junto al Tipo (como en resina): los dos datos de cabecera de compra.
  { key: 'nominal_netto_full_weight', section: 'identity', tier: 'basic', control: 'number',
    unit: 'g', min: 0, max: 100000, step: 1, placeholder: '1000',
    labelKey: 'catalog.fieldWeight', helpKey: 'catalog.helpWeight' },
  { key: 'gtin', section: 'identity', tier: 'advanced', control: 'number', slot: 'aside',
    placeholder: '8412345678901', labelKey: 'catalog.fieldGtin', helpKey: 'catalog.helpGtin' },
  { key: 'brand_specific_material_id', section: 'identity', tier: 'advanced', control: 'text', maxLength: 64, slot: 'aside',
    placeholder: 'MAT-001', labelKey: 'catalog.fieldSkuMaterial', helpKey: 'catalog.helpSkuMaterial' },
  { key: 'brand_specific_package_id', section: 'identity', tier: 'advanced', control: 'text', maxLength: 64, slot: 'aside',
    placeholder: 'PKG-1KG-BLK', labelKey: 'catalog.fieldSkuPackage', helpKey: 'catalog.helpSkuPackage' },
  // URL goes last: a full-width row below the codes, clickable (control 'url').
  { key: 'url', section: 'identity', tier: 'advanced', control: 'url', maxLength: 500, slot: 'full',
    placeholder: 'https://…', labelKey: 'catalog.fieldUrl', helpKey: 'catalog.helpUrl' },

  // ── Apariencia (image moved to Identidad) ──────────────────────────────
  { key: 'primary_color_hex', section: 'appearance', tier: 'basic', control: 'colors',
    placeholder: '#0099E6, #FFFFFF', labelKey: 'catalog.fieldColorsHex', helpKey: 'catalog.helpColors' },
  { key: 'tags', section: 'appearance', tier: 'basic', control: 'tags',
    labelKey: 'catalog.fieldTags', helpKey: 'catalog.helpTags' },

  // ── Material y técnica (solo avanzado en filamento — el peso vive ahora en
  //    Identidad; la dureza ocupa el hueco que dejó el peso). 3 columnas. ─────
  { key: 'filament_diameter', section: 'material', tier: 'advanced', control: 'number',
    unit: 'mm', min: 0, max: 9.99, step: 0.01, placeholder: '1.75',
    labelKey: 'catalog.fieldDiameter', helpKey: 'catalog.helpDiameter' },
  { key: 'hardness', section: 'material', tier: 'advanced', control: 'text', maxLength: 16,
    placeholder: '95 A',
    labelKey: 'catalog.fieldHardness', helpKey: 'catalog.helpHardness' },
  { key: 'nominal_full_length', section: 'material', tier: 'advanced', control: 'number',
    unit: 'mm', min: 0, max: 10000000, step: 1, placeholder: '330000',
    labelKey: 'catalog.fieldFullLength', helpKey: 'catalog.helpFullLength' },
  { key: 'density', section: 'material', tier: 'advanced', control: 'text', maxLength: 24,
    unit: 'g/cm³', placeholder: '1.24',
    labelKey: 'catalog.fieldDensity', helpKey: 'catalog.helpDensity' },
  { key: 'certifications', section: 'material', tier: 'advanced', control: 'certifications',
    labelKey: 'catalog.fieldCertifications', helpKey: 'catalog.helpCertifications' },

  // ── Temperaturas (impresión) — 3 columns ───────────────────────────────
  { key: 'min_print_temperature', section: 'printing', tier: 'advanced', control: 'number',
    unit: '°C', min: 0, max: 500, step: 1, placeholder: '190',
    labelKey: 'catalog.fieldPrintTempMin', helpKey: 'catalog.helpPrintTemp' },
  { key: 'max_print_temperature', section: 'printing', tier: 'advanced', control: 'number',
    unit: '°C', min: 0, max: 500, step: 1, placeholder: '220',
    labelKey: 'catalog.fieldPrintTempMax', helpKey: 'catalog.helpPrintTemp' },
  { key: 'preheat_temperature', section: 'printing', tier: 'advanced', control: 'number',
    unit: '°C', min: 0, max: 500, step: 1, placeholder: '210',
    labelKey: 'catalog.fieldPreheat', helpKey: 'catalog.helpPreheatTemp' },
  { key: 'min_bed_temperature', section: 'printing', tier: 'advanced', control: 'number',
    unit: '°C', min: 0, max: 300, step: 1, placeholder: '50',
    labelKey: 'catalog.fieldBedMin', helpKey: 'catalog.helpBedTemp' },
  { key: 'max_bed_temperature', section: 'printing', tier: 'advanced', control: 'number',
    unit: '°C', min: 0, max: 300, step: 1, placeholder: '60',
    labelKey: 'catalog.fieldBedMax', helpKey: 'catalog.helpBedTemp' },
  { key: 'chamber_temperature', section: 'printing', tier: 'advanced', control: 'number',
    unit: '°C', min: 0, max: 300, step: 1, placeholder: '0',
    labelKey: 'catalog.fieldChamberTemp', helpKey: 'catalog.helpChamberTemp' },
  { key: 'max_print_speed', section: 'printing', tier: 'advanced', control: 'number',
    unit: 'mm/s', min: 0, max: 3000, step: 1, placeholder: '120',
    labelKey: 'catalog.fieldPrintSpeed', helpKey: 'catalog.helpPrintSpeed' },

  // ── Secado ─────────────────────────────────────────────────────────────
  { key: 'drying_temperature', section: 'drying', tier: 'advanced', control: 'number',
    unit: '°C', min: 0, max: 300, step: 1, placeholder: '45',
    labelKey: 'catalog.fieldDryingTemp', helpKey: 'catalog.helpDryingTemp' },
  { key: 'drying_time', section: 'drying', tier: 'advanced', control: 'number',
    unit: 'min', min: 0, max: 10000, step: 1, placeholder: '480',
    labelKey: 'catalog.fieldDryingTime', helpKey: 'catalog.helpDryingTime' },

  // ── Notas (avanzado) ───────────────────────────────────────────────────
  { key: 'notes', section: 'notes', tier: 'advanced', control: 'textarea',
    labelKey: 'catalog.fieldNotes', helpKey: 'catalog.helpNotes' },
] as const;

/**
 * Resin family options for the resin `material_type` select — the resin
 * families of `materialPolymers` (Standard, ABS-Like, Water-Washable…; one
 * water-washable family, not one per chemistry). Special PROPERTIES
 * (transparent, high_speed, high_temperature, …) live in `tags`, NOT here.
 * `material_type` is a free-text column (varchar 40), so the select only
 * constrains NEW input — any pre-existing free-text value still round-trips.
 */
export const MASTER_RESIN_TYPES: readonly string[] = RESIN_POLYMER_LIST;

/** Filament families a catalogue row can pick — the ones with a default €/kg
 *  (`materialPolymers`), not the whole OpenPrintTag list. */
export const MASTER_FILAMENT_TYPES: readonly string[] = FILAMENT_POLYMER_LIST;

/**
 * The type options for ONE row's form: the family list, plus the row's own
 * stored type when it is not one of them (a value from before the families, an
 * OpenPrintTag import). Without it the select shows «—» for that row, and
 * saving after picking anything else overwrites what was stored.
 */
export function materialTypeOptionsFor(list: readonly string[], current: string | null | undefined): readonly string[] {
    return current && !list.includes(current) ? [...list, current] : list;
}

/**
 * Resin (SLA) field-spec. ``material_type`` is a SELECT over
 * `MASTER_RESIN_TYPES` (resin families). Adds the resin-only container volume,
 * viscosity (4 temps) and UV-curing fields; no temperatures / drying.
 * ``is_active`` handled by the shell's status control.
 */
export const RESIN_FIELDS: readonly MaterialFieldSpec[] = [
  // ── Identidad (bespoke layout: tall image on the left, 2-col grid on the
  //    right; the `aside` fields — GTIN + SKUs — form a 3-col row below.
  //    Mirrors FILAMENT_FIELDS) ───────────────────────────────────────────
  { key: 'image_url', section: 'identity', tier: 'basic', control: 'image',
    labelKey: 'catalog.fieldImage', helpKey: 'catalog.helpResinImage' },
  { key: 'manufacturer_id', section: 'identity', tier: 'basic', control: 'select', optionsKey: 'manufacturers', span: 2,
    required: true, labelKey: 'catalog.fieldManufacturer', helpKey: 'catalog.helpManufacturer' },
  { key: 'material_name', section: 'identity', tier: 'basic', control: 'text', maxLength: 80, span: 2,
    required: true, placeholder: 'Aqua-Grey 4K', labelKey: 'catalog.fieldMaterialName', helpKey: 'catalog.helpResinName' },
  { key: 'material_type', section: 'identity', tier: 'basic', control: 'select', optionsKey: 'materialTypes',
    labelKey: 'catalog.fieldMaterialType', helpKey: 'catalog.helpResinType' },
  // Peso junto al Tipo: los dos datos de cabecera de compra (a la derecha del tipo).
  { key: 'nominal_netto_full_weight', section: 'identity', tier: 'basic', control: 'number',
    unit: 'g', min: 0, max: 20000, step: 1, placeholder: '1000',
    labelKey: 'catalog.fieldWeight', helpKey: 'catalog.helpWeight' },
  { key: 'gtin', section: 'identity', tier: 'advanced', control: 'number', slot: 'aside',
    placeholder: '8412345678901', labelKey: 'catalog.fieldGtin', helpKey: 'catalog.helpGtin' },
  { key: 'brand_specific_material_id', section: 'identity', tier: 'advanced', control: 'text', maxLength: 64, slot: 'aside',
    placeholder: 'MAT-001', labelKey: 'catalog.fieldSkuMaterial', helpKey: 'catalog.helpSkuMaterial' },
  { key: 'brand_specific_package_id', section: 'identity', tier: 'advanced', control: 'text', maxLength: 64, slot: 'aside',
    placeholder: 'PKG-1L-GRY', labelKey: 'catalog.fieldSkuPackage', helpKey: 'catalog.helpResinSkuPackage' },
  // URL goes last: a full-width row below the codes, clickable (control 'url').
  { key: 'url', section: 'identity', tier: 'advanced', control: 'url', maxLength: 500, slot: 'full',
    placeholder: 'https://…', labelKey: 'catalog.fieldUrl', helpKey: 'catalog.helpUrl' },

  // ── Apariencia (image moved to Identidad) ──────────────────────────────
  { key: 'primary_color_hex', section: 'appearance', tier: 'basic', control: 'colors',
    placeholder: '#8899AA', labelKey: 'catalog.fieldColorsHex', helpKey: 'catalog.helpColors' },
  { key: 'tags', section: 'appearance', tier: 'basic', control: 'tags',
    labelKey: 'catalog.fieldTags', helpKey: 'catalog.helpTags' },

  // ── Material y técnica (3 col). El peso se movió a Identidad; la longitud de
  //    onda ocupa su sitio en básico; la viscosidad va a la derecha de la dureza.
  //    Las secciones Viscosidad y Curado UV desaparecen — sus campos viven aquí.
  //    La unidad nm va en la etiqueta (fieldWavelength), no como sufijo, para no
  //    duplicar "nm". ───────────────────────────────────────────────────────
  { key: 'cure_wavelength', section: 'material', tier: 'basic', control: 'number',
    min: 300, max: 500, step: 1, placeholder: '405',
    labelKey: 'catalog.fieldWavelength', helpKey: 'catalog.helpWavelength' },
  { key: 'density', section: 'material', tier: 'advanced', control: 'text', maxLength: 24,
    unit: 'g/cm³', placeholder: '1.10',
    labelKey: 'catalog.fieldDensity', helpKey: 'catalog.helpDensity' },
  { key: 'container_volumetric_capacity', section: 'material', tier: 'advanced', control: 'number',
    unit: 'ml', min: 0, max: 20000, step: 1, placeholder: '1000',
    labelKey: 'catalog.fieldVolume', helpKey: 'catalog.helpVolume' },
  { key: 'hardness', section: 'material', tier: 'advanced', control: 'text', maxLength: 16,
    placeholder: '84 D',
    labelKey: 'catalog.fieldHardness', helpKey: 'catalog.helpHardness' },
  { key: 'viscosity', section: 'material', tier: 'advanced', control: 'text', maxLength: 32,
    placeholder: '150–200 mPa·s @25°C',
    labelKey: 'catalog.fieldViscosity', helpKey: 'catalog.helpViscosity' },
  { key: 'certifications', section: 'material', tier: 'advanced', control: 'certifications',
    labelKey: 'catalog.fieldCertifications', helpKey: 'catalog.helpCertifications' },

  // ── Extras (avanzado) ──────────────────────────────────────────────────
  { key: 'notes', section: 'notes', tier: 'advanced', control: 'textarea',
    labelKey: 'catalog.fieldNotes', helpKey: 'catalog.helpNotes' },
] as const;

/**
 * Manufacturer (brand) field-spec. NOT a material — no manufacturer select,
 * material type, colors or tags. The brand LOGO is an uploaded image (key
 * `logo_url`, served from S3 like material images) so the identity section uses
 * the bespoke image-left layout: logo on the left, then Nombre + País stacked,
 * with Web as a full-width row below. The editable `slug` is admin-only (the
 * stable import key for the global catalog) and the tenant wrapper filters it
 * out — workshop brands auto-derive it from the name. The 'links' (OpenPrintTag
 * URL templates) fields are admin-only too; the tenant wrapper drops them.
 */
export const MANUFACTURER_FIELDS: readonly MaterialFieldSpec[] = [
  // Logo first → triggers the identity bespoke layout (image left, fields right).
  { key: 'logo_url', section: 'identity', tier: 'basic', control: 'image',
    labelKey: 'catalog.fieldLogo', helpKey: 'catalog.helpLogo' },
  { key: 'name', section: 'identity', tier: 'basic', control: 'text', maxLength: 100, span: 2,
    required: true, placeholder: 'Prusament', labelKey: 'catalog.fieldBrandName', helpKey: 'catalog.helpBrandName' },
  { key: 'slug', section: 'identity', tier: 'basic', control: 'slug', maxLength: 60, span: 2,
    labelKey: 'catalog.fieldSlug', helpKey: 'catalog.helpSlug' },
  { key: 'country', section: 'identity', tier: 'basic', control: 'text', maxLength: 2, span: 2,
    placeholder: 'ES, DE, CN…', labelKey: 'catalog.fieldCountry', helpKey: 'catalog.helpCountry' },
  { key: 'website', section: 'identity', tier: 'basic', control: 'url', maxLength: 200, slot: 'full',
    placeholder: 'https://…', labelKey: 'catalog.fieldWebsite', helpKey: 'catalog.helpWebsite' },

  { key: 'material_url_template', section: 'links', tier: 'basic', control: 'text', maxLength: 255,
    placeholder: 'https://…/{id}/', labelKey: 'catalog.fieldMaterialUrlTemplate', helpKey: 'catalog.helpMaterialUrlTemplate' },
  { key: 'material_package_url_template', section: 'links', tier: 'basic', control: 'text', maxLength: 255,
    placeholder: 'https://…/{id}/', labelKey: 'catalog.fieldMaterialPackageUrlTemplate', helpKey: 'catalog.helpMaterialPackageUrlTemplate' },

  { key: 'description', section: 'notes', tier: 'basic', control: 'textarea',
    labelKey: 'catalog.fieldNotes', helpKey: 'catalog.helpManufacturerNotes' },
] as const;

/**
 * Supply / spare field-spec (no Basic/Advanced). NOT a material. `kind`
 * (supply/spare) is locked by the entry point (separate tabs) so it's NOT a
 * field here — the wrapper sets it. The wrapper filters `compatible_device`
 * out for kind='supply'. Manufacturer is optional. Slug is editable.
 */
export const SUPPLY_FIELDS: readonly MaterialFieldSpec[] = [
  // ── Identidad (bespoke layout: tall image on the left, 2-col grid on the
  //    right; GTIN + Part Number share a row. Mirrors FILAMENT_FIELDS) ──────
  { key: 'image_url', section: 'identity', tier: 'basic', control: 'image',
    labelKey: 'catalog.fieldImage', helpKey: 'catalog.helpImage' },
  { key: 'manufacturer_id', section: 'identity', tier: 'basic', control: 'select', optionsKey: 'manufacturers', span: 2,
    labelKey: 'catalog.fieldManufacturer', helpKey: 'catalog.helpManufacturer' },
  { key: 'name', section: 'identity', tier: 'basic', control: 'text', maxLength: 120, span: 2,
    placeholder: 'Film FEP 200µm', labelKey: 'catalog.fieldBrandName', helpKey: 'catalog.helpSupplyName' },
  { key: 'slug', section: 'identity', tier: 'basic', control: 'slug', maxLength: 120, span: 2,
    labelKey: 'catalog.fieldSlug', helpKey: 'catalog.helpSlug' },
  { key: 'gtin', section: 'identity', tier: 'basic', control: 'number',
    placeholder: '8412345678901', labelKey: 'catalog.fieldGtin', helpKey: 'catalog.helpGtin' },
  { key: 'part_number', section: 'identity', tier: 'basic', control: 'text', maxLength: 64,
    placeholder: 'FEP-200-X1', labelKey: 'catalog.fieldPartNumber', helpKey: 'catalog.helpPartNumber' },
  { key: 'url', section: 'identity', tier: 'basic', control: 'text', maxLength: 500, span: 2,
    placeholder: 'https://…', labelKey: 'catalog.fieldUrl', helpKey: 'catalog.helpUrl' },
  { key: 'compatible_device', section: 'identity', tier: 'basic', control: 'text', maxLength: 200, span: 2,
    placeholder: 'Bambu X1, Prusa MK4…', labelKey: 'catalog.fieldCompatible', helpKey: 'catalog.helpCompatible' },

  // ── Detalles (descripción) ─────────────────────────────────────────────
  { key: 'description', section: 'notes', tier: 'basic', control: 'textarea',
    labelKey: 'catalog.fieldDescription', helpKey: 'catalog.helpDescription' },
] as const;

/** Sections that have at least one field visible at the given tier. */
export function visibleSections(
  fields: readonly MaterialFieldSpec[],
  showAdvanced: boolean,
): MaterialSectionKey[] {
  const seen = new Set<MaterialSectionKey>();
  for (const f of fields) {
    if (showAdvanced || f.tier === 'basic') seen.add(f.section);
  }
  return MATERIAL_SECTIONS.map((s) => s.key).filter((k) => seen.has(k));
}

/** Fields of a section visible at the given tier, in spec order. */
export function sectionFields(
  fields: readonly MaterialFieldSpec[],
  section: MaterialSectionKey,
  showAdvanced: boolean,
): MaterialFieldSpec[] {
  return fields.filter(
    (f) => f.section === section && (showAdvanced || f.tier === 'basic'),
  );
}
